/**
 * Calcule les montants des lignes de type "subtotal" dans un layout cash-flow.
 *
 * Chaque snapshot expose deux niveaux de lecture :
 *   - Cumul depuis le haut du tableau (`actual_cents`, `forecast_cents`, `budget_cents`) :
 *     somme de toutes les catégories rencontrées depuis le début du layout jusqu'à
 *     cette ligne. Utile pour lire les soldes intermédiaires de gestion en cascade
 *     (CA → Marge brute → EBE → Résultat d'exploitation).
 *   - Somme de section depuis le subtotal précédent (`section_actual_cents`,
 *     `section_forecast_cents`, `section_budget_cents`) : delta entre ce subtotal et
 *     le précédent. Permet d'afficher la contribution du segment courant sans casser
 *     la lecture en cascade.
 *
 * @param {Array} layout — rows ordonnées (kind: 'category' | 'subtotal')
 * @param {Map<string, object>} byCategoryMap — category_id → { per_period: [...] }
 * @param {string[]} periodStarts — liste des period_start ISO
 * @returns {Map<string, Array>} — rowId → perPeriod[] (uniquement pour kind='subtotal')
 */
export function computeSubtotals(layout, byCategoryMap, periodStarts) {
  // Les champs TTC (actual/forecast/budget) sont toujours présents côté backend
  // → init à 0. Les champs HT (actual_ht / forecast_ht) peuvent manquer pour les
  // catégories sans TVA (provisions, transferts internes) → init à null pour que
  // le snapshot retourne null si aucune contribution, ce qui permet au fallback
  // `?? actual_cents` côté UI de se déclencher correctement.
  const makeAcc = () => new Map(
    periodStarts.map((ps) => [ps, {
      actual_cents:      0,
      forecast_cents:    0,
      budget_cents:      0,
      actual_ht_cents:   null,
      forecast_ht_cents: null,
    }])
  );

  const cumAcc     = makeAcc();
  let   sectionAcc = makeAcc();

  const result = new Map();

  for (const row of layout) {
    if (row.kind === 'category') {
      const data = byCategoryMap.get(row.category_id);
      if (!data?.per_period) continue;

      for (const pp of data.per_period) {
        const c = cumAcc.get(pp.period_start);
        const s = sectionAcc.get(pp.period_start);
        if (!c || !s) continue;
        if (pp.actual_cents      != null) { c.actual_cents      += pp.actual_cents;      s.actual_cents      += pp.actual_cents; }
        if (pp.forecast_cents    != null) { c.forecast_cents    += pp.forecast_cents;    s.forecast_cents    += pp.forecast_cents; }
        if (pp.budget_cents      != null) { c.budget_cents      += pp.budget_cents;      s.budget_cents      += pp.budget_cents; }
        if (pp.actual_ht_cents   != null) {
          c.actual_ht_cents   = (c.actual_ht_cents   ?? 0) + pp.actual_ht_cents;
          s.actual_ht_cents   = (s.actual_ht_cents   ?? 0) + pp.actual_ht_cents;
        }
        if (pp.forecast_ht_cents != null) {
          c.forecast_ht_cents = (c.forecast_ht_cents ?? 0) + pp.forecast_ht_cents;
          s.forecast_ht_cents = (s.forecast_ht_cents ?? 0) + pp.forecast_ht_cents;
        }
      }
    } else if (row.kind === 'subtotal') {
      const snapshot = periodStarts.map((ps) => {
        const c = cumAcc.get(ps);
        const s = sectionAcc.get(ps);
        return {
          period_start:              ps,
          actual_cents:              c?.actual_cents      ?? 0,
          forecast_cents:            c?.forecast_cents    ?? 0,
          budget_cents:              c?.budget_cents      ?? 0,
          actual_ht_cents:           c?.actual_ht_cents   ?? null,
          forecast_ht_cents:         c?.forecast_ht_cents ?? null,
          section_actual_cents:      s?.actual_cents      ?? 0,
          section_forecast_cents:    s?.forecast_cents    ?? 0,
          section_budget_cents:      s?.budget_cents      ?? 0,
          section_actual_ht_cents:   s?.actual_ht_cents   ?? null,
          section_forecast_ht_cents: s?.forecast_ht_cents ?? null,
        };
      });
      result.set(row.id, snapshot);
      // Reset l'accumulateur de section pour le segment suivant
      sectionAcc = makeAcc();
    }
  }

  return result;
}
